import * as React from 'react';
import classNames from 'classnames';
import SideModal from 'sharedComponents/SideModal/SideModal';
import Tabs, {
  TabContent,
  Body,
  Footer
} from 'sharedComponents/SideModal/Tabs';
import Button from 'sharedComponents/core/Input/Button';
import EasyInput from 'components/core/Input/EasyInput';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from 'helpers/theme';
import { UpdateManager, UpdateCompany } from './mutations';
import { mutations_UpdateManagerMutationResponse } from './__generated__/mutations_UpdateManagerMutation.graphql';
import { mutations_UpdateCompanyMutationResponse } from './__generated__/mutations_UpdateCompanyMutation.graphql';
import { createFragmentContainer, graphql } from 'react-relay';
import { ManagerSlideIn_manager } from './__generated__/ManagerSlideIn_manager.graphql';
import Spacer from 'sharedComponents/core/Spacers/Spacer';
import UploadProfilePicture from '../core/UploadProfilePicture/UploadProfilePicture';

const useStyles = createUseStyles((theme: Theme) => ({
  personalContainer: {
    display: 'flex',
    marginTop: theme.spacing(2),
    marginBottom: 35
  },
  heading: {
    marginBottom: theme.spacing(2),
    color: theme.colors.primaryBlack,
    fontSize: theme.fontSizes.heading,
    fontWeight: 800
  },
  profilePicContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '30%',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(3) //"5%"
  },
  editPicPencilIcon: {
    marginBottom: -30,
    marginRight: -65,
    zIndex: 15
  },
  editPhotoBtn: {
    marginTop: theme.spacing(1)
  },
  personalDetailContainer: {
    display: 'flex',
    flexDirection: 'column'
  },
  namesContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: theme.spacing(2)
  },
  marginRight: {
    marginRight: theme.spacing(2)
  },
  marginBottom: {
    marginBottom: theme.spacing(2)
  },
  contactContainer: {
    display: 'flex',
    marginTop: theme.spacing(0),
    marginBottom: 35
  },
  emailInput: {
    marginRight: '7.5%',
    width: '40%'
  },
  telephoneInput: {
    width: '40%'
  },
  footerBtnsContainer: {
    display: 'flex'
  },
  submitBtn: {
    marginLeft: theme.spacing(2)
  },
  halfSize: {
    width: '50%'
  },
  input: {
    display: 'none'
  }
}));

const delegateDetails: TabContent[] = [
  {
    key: 'Your Details',
    component: ({ state, setState, setTab, closeModal }) => {
      const theme = useTheme();
      const classes = useStyles({ theme });

      return (
        <>
          <Body>
            <span className={classes.heading}>Personal</span>
            <div className={classes.personalContainer}>
              <UploadProfilePicture
                title="Edit Photo"
                url={state.profileImageUrl}
                onUploaded={(token: string, url: string) => {
                  setState((s: object) => ({
                    ...s,
                    profileImageToken: token,
                    profileImageUrl: url,
                  }))
                }}
              />
              <div className={classes.personalDetailContainer}>
                <div className={classes.namesContainer}>
                  <div className={classes.marginRight}>
                    <EasyInput
                      label="First Name"
                      placeholder="e.g. John"
                      value={state.firstName}
                      onChange={(firstName) =>
                        setState((s: object) => ({ ...s, firstName }))
                      }
                    />
                  </div>
                  <EasyInput
                    label="Last Name"
                    placeholder="e.g. Smith"
                    value={state.lastName}
                    onChange={(lastName) =>
                      setState((s: object) => ({ ...s, lastName }))
                    }
                  />
                </div>
              </div>
            </div>
            <span className={classes.heading}>Contact</span>
            <div className={classes.contactContainer}>
              <div className={classes.emailInput}>
                <EasyInput
                  label="Email"
                  type="email"
                  placeholder="e.g. example@fedex.com"
                  value={state.email}
                  onChange={(email) =>
                    setState((s: object) => ({ ...s, email }))
                  }
                />
              </div>
              <div className={classes.telephoneInput}>
                <EasyInput
                  label="telephone number"
                  type="tel"
                  value={state.telephone}
                  placeholder="e.g. +44 1234 567890"
                  onChange={(telephone) =>
                    setState((s: object) => ({ ...s, telephone }))
                  }
                />
              </div>
            </div>
          </Body>
          <Footer>
            <div />
            <div className={classes.footerBtnsContainer}>
              <Button archetype="default" onClick={() => closeModal()}>
                Cancel
              </Button>
              <Button
                archetype="submit"
                onClick={() => {
                  const manager = {
                    uuid: state.uuid,
                    firstName: state.firstName,
                    lastName: state.lastName,
                    jobTitle: state.jobTitle,
                    email: state.email,
                    telephone: state.telephone,
                    profileImageToken: state.profileImageToken
                  };
                  state.onManager(manager, (resp: ManagerInfo) => {
                    setState((s: object) => ({
                      ...s,
                      firstName: resp.firstName,
                      lastName: resp.lastName,
                      jobTitle: resp.jobTitle,
                      email: resp.email,
                      telephone: resp.telephone,
                      profileImageUrl: resp.profileImageUrl
                    }));
                    closeModal();
                  }
                  );
                }}
                className={classes.submitBtn}
              >
                Update
              </Button>
            </div>
          </Footer>
        </>
      );
    }
  },
  {
    key: 'Company Details',
    component: ({ state, setState, setTab, closeModal }) => {
      const theme = useTheme();
      const classes = useStyles({ theme });

      return (
        <>
          <Body>
            <span className={classes.heading}>Company</span>
            <div className={classes.personalDetailContainer}>
              <div className={classes.marginBottom}>
                <EasyInput
                  label="Company Name"
                  placeholder="Company Name"
                  value={state.companyName}
                  onChange={(companyName) =>
                    setState((s: object) => ({ ...s, companyName }))
                  }
                />
              </div>
              <div className={classes.marginBottom}>
                <EasyInput
                  label="Company Contact Email"
                  placeholder="Company Email"
                  type="email"
                  value={state.contactEmail}
                  onChange={(contactEmail) =>
                    setState((s: object) => ({ ...s, contactEmail }))
                  }
                />
              </div>
            </div>
            <Spacer vertical spacing={3} />
            <span className={classes.heading}>Billing Address</span>
            <div className={classes.marginBottom}>
              <EasyInput
                label="Address Line 1"
                value={state.addressLine1}
                placeholder="Address Line 1"
                onChange={(addressLine1) =>
                  setState((s: object) => ({ ...s, addressLine1 }))
                }
              />
            </div>
            <div className={classes.marginBottom}>
              <EasyInput
                label="Address Line 2"
                value={state.addressLine2}
                placeholder="Address Line 2"
                onChange={(addressLine2) =>
                  setState((s: object) => ({ ...s, addressLine2 }))
                }
              />
            </div>
            <div className={classes.contactContainer}>
              <div
                className={classNames(classes.marginRight, classes.halfSize)}
              >
                <EasyInput
                  label="County"
                  placeholder="County"
                  value={state.county}
                  onChange={(county) =>
                    setState((s: object) => ({ ...s, county }))
                  }
                />
              </div>
              <div className={classes.halfSize}>
                <EasyInput
                  label="Post Code"
                  value={state.postCode}
                  placeholder="Post Code"
                  onChange={(postCode) =>
                    setState((s: object) => ({ ...s, postCode }))
                  }
                />
              </div>
            </div>
          </Body>
          <Footer>
            <div />
            <div className={classes.footerBtnsContainer}>
              <Button archetype="default" onClick={() => closeModal()}>
                Cancel
              </Button>
              <Button
                archetype="submit"
                onClick={() => {
                  const company = {
                    uuid: state.companyUUID,
                    companyName: state.companyName,
                    contactEmail: state.contactEmail,
                    addressLine1: state.addressLine1,
                    addressLine2: state.addressLine2,
                    county: state.county,
                    postCode: state.postCode
                  };
                  state.onCompany(company, (resp: CompanyInfo) => {
                    setState((s: object) => ({
                      ...s,
                      companyName: resp.companyName,
                      contactEmail: resp.contactEmail,
                      addressLine1: resp.addressLine1,
                      addressLine2: resp.addressLine2,
                      county: resp.county,
                      postCode: resp.postCode
                    }));
                    closeModal();
                  }
                  );
                }}
                className={classes.submitBtn}
              >
                Update
              </Button>
            </div>
          </Footer>
        </>
      );
    }
  }
];

export type ManagerInfo = {
  uuid: string;
  firstName: string;
  lastName: string;
  jobTitle: string;
  email: string;
  telephone: string;
  profileImageUrl: string;
  profileImageToken?: string;
};

export type CompanyInfo = {
  uuid: String;
  companyName: String;
  contactEmail: String;
  addressLine1: String;
  addressLine2: String;
  county: String;
  postCode: String;
};

type Props = {
  isOpen: boolean;
  manager: ManagerSlideIn_manager;
  refetch: () => void;
  onClose: () => void;
};

const ManagerSlideIn = ({ isOpen, manager, refetch, onClose }: Props) => {
  const updateManager = (
    manager: ManagerInfo,
    stateUpdate: (resp: ManagerInfo) => void
  ) =>
    UpdateManager(
      {
        uuid: manager.uuid,
        firstName: manager.firstName,
        lastName: manager.lastName,
        jobTitle: manager.jobTitle,
        email: manager.email,
        telephone: manager.telephone,
        profileImageToken: manager.profileImageToken
      },
      () => {},
      (resp: mutations_UpdateManagerMutationResponse) => {
        stateUpdate({
          uuid: manager.uuid,
          firstName: resp.updateManager?.firstName ?? '',
          lastName: resp.updateManager?.lastName ?? '',
          jobTitle: resp.updateManager?.jobTitle ?? '',
          email: resp.updateManager?.email ?? '',
          telephone: resp.updateManager?.telephone ?? '',
          profileImageUrl: resp.updateManager?.profileImageUrl ?? '',
        });
        refetch();
      }
    );

  const updateCompany = (
    company: CompanyInfo,
    stateUpdate: (resp: CompanyInfo) => void
  ) => {
    UpdateCompany(
      {
        uuid: company.uuid,
        name: company.companyName,
        contactEmail: company.contactEmail,
        addressLine1: company.addressLine1,
        addressLine2: company.addressLine2,
        county: company.county,
        postCode: company.postCode
      },
      () => {},
      (resp: mutations_UpdateCompanyMutationResponse) => {
        stateUpdate({
          uuid: resp.updateCompany?.uuid ?? '',
          companyName: resp.updateCompany?.name ?? '',
          contactEmail: resp.updateCompany?.contactEmail ?? '',
          addressLine1: resp.updateCompany?.address?.addressLine1 ?? '',
          addressLine2: resp.updateCompany?.address?.addressLine2 ?? '',
          county: resp.updateCompany?.address?.county ?? '',
          postCode: resp.updateCompany?.address?.postCode ?? ''
        });
        refetch();
      }
    );
  };

  return (
    <SideModal
      title={`${manager.firstName} ${manager.lastName}`}
      closeModal={onClose}
      isOpen={isOpen}
    >
      <Tabs
        content={delegateDetails}
        closeModal={onClose}
        initialState={{
          // Manager Details
          profileImageUrl: manager.profileImageUrl,
          uuid: manager.uuid,
          firstName: manager.firstName,
          lastName: manager.lastName,
          jobTitle: manager.jobTitle,
          email: manager.email,
          telephone: manager.telephone,
          onManager: updateManager,
          onCompany: updateCompany,
          // Company Details
          companyUUID: manager.company.uuid,
          companyName: manager.company.name,
          contactEmail: manager.company.contactEmail,
          addressLine1: manager.company.address.addressLine1,
          addressLine2: manager.company.address.addressLine2,
          county: manager.company.address.county,
          postCode: manager.company.address.postCode
        }}
      />
    </SideModal>
  );
};

export default createFragmentContainer(ManagerSlideIn, {
  manager: graphql`
    fragment ManagerSlideIn_manager on Manager {
      uuid
      firstName
      lastName
      email
      jobTitle
      telephone
      profileImageUrl
      company {
        logoURL
        uuid
        name
        contactEmail
        address {
          addressLine1
          addressLine2
          county
          postCode
        }
      }
    }
  `
});
